/**
 * Launcher mode (roadmap Section 32): the native Launcher plugin
 * (native/android/.../launcher/LauncherPlugin.java). Inside the Android app only;
 * everywhere else `launcherAvailable()` is false and nothing here is called.
 */

export interface LauncherApp {
  package: string;
  label: string;
  /** A TV app (LEANBACK_LAUNCHER). */
  tv: boolean;
  /** Has a phone-style entry (LAUNCHER). A phone-only app is listed too, marked (owner, 2026-10-20). */
  phone: boolean;
  system: boolean;
  banner: boolean;
}

export interface LauncherInput {
  id: string;
  type: string;
  label: string;
  /** Firmware can list ports the box lacks (the projector shows three HDMI, has one). */
  state: "connected" | "standby" | "disconnected";
}

export interface LauncherStatus {
  transport: "wifi" | "ethernet" | "cellular" | "other" | "none";
  wifiPresent?: boolean;
  wifiOn?: boolean | null;
  /** 0-4 bars. */
  wifiSignalLevel?: number;
  bluetoothPresent: boolean;
  bluetoothOn: boolean;
  address: string | null;
}

export interface LauncherHome {
  apps: LauncherApp[];
  inputs: LauncherInput[];
  tiles: { settings: boolean; playStore: boolean; downloader: boolean };
  /** Other app stores already installed (Aptoide TV, APKPure, F-Droid...), never linked to by us. */
  stores?: Array<{ package: string; label: string }>;
  status: LauncherStatus;
}

export interface LauncherSound {
  volume: number;
  max: number;
  fixed: boolean;
}

export interface LauncherQuick extends LauncherStatus {
  sound: LauncherSound;
  storage: { freeBytes: number; totalBytes: number };
  boxName: string;
  android: string;
  /** Which of Android's screens this box can open (LauncherPlugin.SCREENS). */
  screens: Record<string, boolean>;
}

/** Android settings screens the launcher can open, by LauncherPlugin.SCREENS key. */
export type SettingsScreen = "all" | "network" | "accessories" | "displaysound" | "storage" | "apps" | "about";

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

const PLUGIN = "Launcher";

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  return cap.PluginHeaders?.some((header) => header.name === PLUGIN) ? cap : null;
}

/** Whether this app can be a launcher here: the Android app with the Launcher plugin. */
export function launcherAvailable(): boolean {
  return capacitor() !== null;
}

function call<T>(method: string, options: Record<string, unknown> = {}): Promise<T> {
  const cap = capacitor();
  if (!cap) return Promise.reject(new Error("unavailable"));
  return cap.nativePromise!(PLUGIN, method, options) as Promise<T>;
}

export const launcher = {
  home: () => call<LauncherHome>("home"),
  quick: () => call<LauncherQuick>("quick"),
  openApp: (pkg: string) => call<void>("openApp", { package: pkg }),
  openSettings: (key: SettingsScreen) => call<void>("openSettings", { key }),
  openInput: (id: string) => call<void>("openInput", { id }),
  setVolume: (level: number) => call<LauncherSound>("volume", { level }),
  toggleMute: () => call<LauncherSound>("volume", { toggleMute: true }),
};

/** An app's banner (or icon), drawn and kept natively (launcher/AppArt.java). */
export function appArtUrl(pkg: string): string {
  return `/_native/launcher/art?pkg=${encodeURIComponent(pkg)}`;
}
